// The three-beat first run, played on the camera itself: see a hand → open it
// and hear a note → move it and hear the note change. Pure state so it can be
// tested without a DOM.

export type FirstRunBeat = 'raise' | 'open' | 'move' | 'complete'

export interface FirstRunObservation {
  handVisible: boolean
  playing: boolean
  noteIndex: number | null
}

export interface FirstRunCopy {
  title: string
  copy: string
}

const COPY: Record<FirstRunBeat, FirstRunCopy> = {
  raise: { title: 'Raise a hand.', copy: 'Hold it up where the camera can see it. Each hand becomes a voice.' },
  open: { title: 'Open your hand.', copy: 'An open hand plays. A fist rests. Your index finger points at the note.' },
  move: { title: 'Now move it up and down.', copy: 'Higher finger, higher note. Push toward the camera to swell, pull back to soften.' },
  complete: { title: 'Each hand is a voice.', copy: 'Point with your index finger — higher is higher. An open hand plays, a fist rests. Push toward the camera to swell. Enable Layers, then quick pinch to record a loop. Hold a pinch: hush the echoes. Hold two fingers up: next voice. Hold a thumbs-up: echo on or off.' }
}

export class FirstRun {
  private current: FirstRunBeat
  private firstNote: number | null = null
  private visibleSince: number | null = null

  constructor(completed = false) {
    this.current = completed ? 'complete' : 'raise'
  }

  get beat(): FirstRunBeat { return this.current }
  get copy(): FirstRunCopy { return COPY[this.current] }
  isComplete(): boolean { return this.current === 'complete' }

  observe(observation: FirstRunObservation, timestampMs: number): boolean {
    const before = this.current
    if (this.current === 'raise') {
      if (!observation.handVisible) this.visibleSince = null
      else if (this.visibleSince === null) this.visibleSince = timestampMs
      else if (timestampMs - this.visibleSince >= 400) this.current = 'open'
    } else if (this.current === 'open') {
      if (observation.playing) { this.current = 'move'; this.firstNote = observation.noteIndex }
    } else if (this.current === 'move') {
      if (observation.playing && observation.noteIndex !== null && this.firstNote !== null && Math.abs(observation.noteIndex - this.firstNote) >= 2) this.current = 'complete'
    }
    return before !== this.current
  }

  skip(): void { this.current = 'complete' }

  restart(): void {
    this.current = 'raise'
    this.firstNote = null
    this.visibleSince = null
  }
}
